// src/features/system/auth/api/authApi.ts
import { fetchData } from '../../../../utils/fetch';

/* In-memory token store */
let ACCESS_TOKEN: string | null = null;
let REFRESH_TOKEN: string | null = null;

export const auth = {
  get access()  { return ACCESS_TOKEN; },
  get refresh() { return REFRESH_TOKEN; },
  set(access: string | null, refresh?: string | null) {
    ACCESS_TOKEN = access ?? null;
    if (refresh !== undefined) REFRESH_TOKEN = refresh ?? null;
  },
  clear() { ACCESS_TOKEN = null; REFRESH_TOKEN = null; },
};

/* ── helpers: always return a plain string->string map ─────────────── */
type HeadersDict = Record<string, string>;

function authHeader(): HeadersDict {
  return ACCESS_TOKEN ? { Authorization: `Bearer ${ACCESS_TOKEN}` } : {};
}
function withJson(h: HeadersDict = {}): HeadersDict {
  return { 'Content-Type': 'application/json', ...h };
}

/* DTOs */
export interface LoginPayload { username: string; password: string; }
export interface LoginResponse { access_token: string; refresh_token: string; }
export interface RegisterPayload { username: string; password: string; tenant_id: number; roles: string[]; }
export interface ChangePasswordPayload { old_password: string; new_password: string; }

/* Login */
export async function loginApi(payload: LoginPayload): Promise<LoginResponse> {
  const raw = await fetchData('/backend_services_rust/auth/login', {
    method: 'POST',
    headers: withJson(),
    body: JSON.stringify(payload),
    skipAuth: true,
  });
  const data = (raw && typeof raw === 'object' && 'data' in raw) ? (raw as any).data : raw;
  if (typeof data?.access_token === 'string' && typeof data?.refresh_token === 'string') {
    auth.set(data.access_token, data.refresh_token);
    return { access_token: data.access_token, refresh_token: data.refresh_token };
  }
  throw new Error('Could not parse login response.');
}

/* Register */
export async function registerApi(payload: RegisterPayload): Promise<{ id_user: number }> {
  const raw = await fetchData('/backend_services_rust/auth/register', {
    method: 'POST',
    headers: withJson(),
    body: JSON.stringify(payload),
    skipAuth: true,
  });
  const data = (raw && typeof raw === 'object' && 'created' in raw) ? (raw as any).created : raw;
  if (typeof data?.id_user === 'number') return { id_user: data.id_user };
  throw new Error('Could not parse register response.');
}

/* Refresh */
export async function refreshTokenApi(): Promise<{ access_token: string }> {
  if (!REFRESH_TOKEN) throw new Error('No refresh token available');
  const raw = await fetchData('/backend_services_rust/auth/refresh', {
    method: 'POST',
    headers: withJson(),
    body: JSON.stringify({ refresh_token: REFRESH_TOKEN }),
    skipAuth: true,
  });
  const data = (raw && typeof raw === 'object' && 'data' in raw) ? (raw as any).data : raw;
  if (typeof data?.access_token === 'string') {
    auth.set(data.access_token); // keep existing refresh
    return { access_token: data.access_token };
  }
  throw new Error('Could not parse refresh response.');
}

/* Logout */
export async function logoutApi(): Promise<void> {
  try {
    await fetchData('/backend_services_rust/auth/logout', {
      method: 'POST',
      headers: withJson(authHeader()),
    });
  } finally {
    auth.clear();
  }
}

/* Password helpers */
export async function changePasswordApi(payload: ChangePasswordPayload): Promise<void> {
  await fetchData('/backend_services_rust/auth/change_password', {
    method: 'POST',
    headers: withJson(authHeader()),
    body: JSON.stringify(payload),
  });
}

export async function resetPasswordApi(): Promise<{ new_password: string }> {
  const raw = await fetchData('/backend_services_rust/auth/reset_password', {
    method: 'POST',
    headers: withJson(authHeader()),
  });
  const data = (raw && typeof raw === 'object' && 'data' in raw) ? (raw as any).data : raw;
  if (typeof data?.new_password === 'string') return { new_password: data.new_password };
  throw new Error('Could not parse reset response.');
}
